// types.ts


export interface AddressDTO {
    street?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    country?: string;
    isPrimary?: boolean;
}

export interface OwnerDetailsDTO {
    names?: string[];
    addresses?: AddressDTO[];
    emails?: string[];
    phoneNumbers?: string[];
    dateOfBirth?: string;
    primaryName?: string;
    primaryAddress?: AddressDTO;
    primaryEmail?: string;
    primaryPhone?: string;
}

export interface AuthDetails {
    itemId: string;
    instId: string;
    accountId: string;
    account: string;
    routing: string;
    // Account type fields
    accountType?: string;        // e.g., "depository", "credit", "investment"
    accountSubtype?: string;     // e.g., "checking", "savings", "credit_card"
    accountName?: string;        // e.g., "Plaid Checking"
    // Identity fields
    accountHolderName?: string;
    owners?: OwnerDetailsDTO[];
    primaryAddress?: AddressDTO;
    primaryEmail?: string;
    primaryPhone?: string;
    taxId?: string;              // Tax ID extracted from form
  }
  

export interface AccountRequestDTO {
    id: number;
    requestedBy: string;
    type: string;
    toEmail: string;
    toPhone: string;
    token: string;
    dateCreated: Date;
    status: string;
    note: string;
    accessToken: string;
    accountNumber: string;
    firstName: string;
    lastName: string;
    referralSource?: string;
    accountType?: string;
    businessName?: string;
    authDetails: AuthDetails;
    accessTokenPresent: boolean;
    externalId: number;
    externalSource: string;
    appStatus?: string;
    appType?: string;
}

export interface DataTableProps {
    data: AccountRequestDTO[];
    onSort: (sortBy: string) => void;
    hasMore: boolean;
    fetchData: () => void;
    sortBy?: string;
    sortDirection?: string; // 'asc', 'desc', 'none'
}

export interface SearchBarProps {
    onSearch: (searchQuery: string) => void;
    onAdvancedToggle?: () => void;
    resetToken: number;
    showAdvancedSearch?: boolean;
}

// Advanced search types matching backend DTOs
export interface AdvancedSearchRequestDTO {
    // Text search fields
    requestedBy?: string;
    toEmail?: string;
    toPhone?: string;
    firstName?: string;
    lastName?: string;
    note?: string;
    accountNumber?: string;
    token?: string;
    referralSource?: string;
    businessName?: string;

    // Dropdown/exact match fields
    accountType?: string;
    status?: string;
    type?: string;
    appStatus?: string;
    appType?: string;
    externalSource?: string;
    
    // Date range fields
    dateCreatedStart?: string;
    dateCreatedEnd?: string;
    
    // Number fields
    id?: number;
    externalId?: number;
    
    // Pagination and sorting
    pageNumber?: number;
    sortBy?: string;
    sortDirection?: string;
}

export enum FieldType {
    TEXT = 'TEXT',
    DROPDOWN = 'DROPDOWN',
    DATE_RANGE = 'DATE_RANGE',
    NUMBER = 'NUMBER'
}

export interface SearchFieldMetadataDTO {
    fieldName: string;
    displayLabel: string;
    fieldType: FieldType;
    dropdownOptions?: string[];
    required?: boolean;
    placeholder?: string;
}

export interface SortStateDTO {
    sortBy: string;
    direction: string; // "asc", "desc", "none"
}

export interface AdvancedSearchResponseDTO {
    results: {
        content: AccountRequestDTO[];
        totalElements: number;
        totalPages: number;
        number: number;
        size: number;
        hasNext: boolean;
    };
    fieldMetadata: SearchFieldMetadataDTO[];
    currentSort: SortStateDTO;
    totalResults: number;
    hasSearchCriteria: boolean;
}

export interface AccountRequestHistoryDTO {
    id: number;
    accountRequestId: number;
    timestamp: Date;
    event: string;
    detail: string;
    user: string;
}

export interface FormData {
    id: number;
    formData: string;
    createDate: string | null;
    updateDate: string | null;
}

export interface FormattedValues {
    [key: string]: string;
}

export interface FieldMapping {
    field: string;
    value: string;
    condition: string;
}

export interface FormSpec {
    id: number;
    name: string;
    pdfMappingJson: FieldMapping[];
    bulkExportMappingJson?: string;
}

export interface FormDTO {
    id: number;
    formkey: string;
    status: string;
    type: string;
    createDate: string;
    submitDate: string;
    completeDate: string | null;
    externalSystem: string;
    externalID: string;
    formSpecId: number | null;
    formData: FormData;
    formattedValues: FormattedValues;
    formSpec: FormSpec;
}

export interface BatchExportItemDTO {
    id: number;
    accountRequestId: number;
    status: string;
    errorDetail?: string;
}

export interface BatchExportDTO {
    id: number;
    batchNumber: string;
    requestedBy: string;
    status: string;
    totalRecords: number;
    processedRecords: number;
    dateCreated: Date;
    dateCompleted?: Date;
    expirationDate?: Date;
    errorMessage?: string;
    items?: BatchExportItemDTO[];
    hasResult: boolean;
    isExpired: boolean;
}

export interface BulkExportDTO {
    accountRequestIds: number[];
}
